import { React, useState } from "react";
import {
	Button,
	Checkbox,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	FormHelperText,
	InputAdornment,
	InputLabel,
	MenuItem,
	Select,
	TextField,
} from "@mui/material";
import dayjs from "dayjs";
import "dayjs/locale/en";
import "dayjs/locale/pl";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useTheme } from "@mui/material/styles";
import {
	useAddTransactionsMutation,
	useGetWalletValueOnDate,
} from "../../services/queryHooks";
import { CategoryDisplay } from "./CategorySelect/CategoryDisplay";
import { Loading } from "../../components/common/Loading";
import { useTranslation } from "react-i18next";
import { validateTextField } from "../../utils/validation";
import {
	validateTransactionDate,
	validateRequiredFields,
	validateWalletBalance,
} from "../../utils/transactionValidation";

export default function AddTransactionModal({
	isOpen,
	onClose,
	categories,
	wallets,
}) {
	const theme = useTheme();
	const { t } = useTranslation();
	const userLanguage = localStorage.getItem("i18nextLng");

	const [title, setTitle] = useState("");
	const [amount, setAmount] = useState("");
	const [selectedCurrency, setSelectedCurrency] = useState("");
	const [selectedDate, setSelectedDate] = useState(dayjs());
	const [selectedCategory, setSelectedCategory] = useState("");
	const [selectedSubcategory, setSelectedSubcategory] = useState("");
	const [selectedWallet, setSelectedWallet] = useState("");
	const [selectedType, setSelectedType] = useState("");
	const [markAsUnimportant, setMarkAsUnimportant] = useState(false);
	const addTransactionMutation = useAddTransactionsMutation();

	const {
		data: walletValueOnDateQuery,
		isPending: walletValueOnDateQueryPending,
	} = useGetWalletValueOnDate(selectedWallet, selectedDate?.toISOString());

	const [errorTitle, setErrorTitle] = useState("");
	const [errorDate, setErrorDate] = useState("");
	const [errorAmount, setErrorAmount] = useState("");
	const [errorWallet, setErrorWallet] = useState("");
	const [errorType, setErrorType] = useState("");
	const [errorCategory, setErrorCategory] = useState("");

	const minDate = dayjs("2000-01-01");
	const maxDate = dayjs();

	const validateInputs = () => {
		setErrorDate("");
		setErrorTitle("");
		setErrorAmount("");
		setErrorWallet("");
		setErrorType("");
		setErrorCategory("");

		let hasErrors = false;
		const walletObj = wallets.find((w) => w._id === selectedWallet);

		const dateError = validateTransactionDate(
			selectedDate,
			walletObj?.createdAt,
			minDate,
			t
		);
		if (dateError) {
			setErrorDate(dateError);
			hasErrors = true;
		}

		const titleError = validateTextField(title, t, {
			fieldName: t("transactionTitle"),
		});
		if (titleError) {
			setErrorTitle(titleError);
			hasErrors = true;
		}

		const requiredErrors = validateRequiredFields(
			{
				amount,
				wallet: selectedWallet,
				type: selectedType,
				category: selectedCategory,
			},
			t
		);
		if (requiredErrors.amount) {
			setErrorAmount(requiredErrors.amount);
			hasErrors = true;
		}
		if (requiredErrors.wallet) {
			setErrorWallet(requiredErrors.wallet);
			hasErrors = true;
		}
		if (requiredErrors.type) {
			setErrorType(requiredErrors.type);
			hasErrors = true;
		}
		if (requiredErrors.category) {
			setErrorCategory(requiredErrors.category);
			hasErrors = true;
		}

		if (selectedType === "expense" && selectedWallet && walletObj) {
			const balanceError = validateWalletBalance(
				amount,
				walletObj.balance,
				walletValueOnDateQuery.data?.value,
				selectedCurrency,
				walletValueOnDateQuery.isLoading,
				walletValueOnDateQuery.error,
				t
			);

			if (balanceError) {
				setErrorAmount(balanceError);
				hasErrors = true;
			}
		}

		return !hasErrors;
	};

	const handleWalletChange = (event) => {
		setSelectedWallet(event.target.value);
		setSelectedCurrency(
			wallets.find((item) => item._id === event.target.value).currency
		);
	};

	const handleTypeChange = (event) => {
		setSelectedType(event.target.value);
		setSelectedCategory("");
		setSelectedSubcategory("");
	};

	const handleBalanceChange = (event) => {
		let value = event.target.value;
		value = value.replace(/[^0-9.,]/g, "");
		value = value.replace(",", ".");
		const regex = /^\d{0,12}(\.\d{0,2})?$/;
		if (!regex.test(value)) return;
		setAmount(value);
	};

	const handleTitleChange = (event) => {
		setTitle(event.target.value);
	};

	const handleBlur = (event) => {
		const value = parseFloat(event.target.value);
		if (!isNaN(value)) {
			event.target.value = value.toFixed(2);
		}
	};

	const submitForm = (event) => {
		event.preventDefault();
		if (
			errorDate ||
			errorTitle ||
			errorAmount ||
			errorWallet ||
			errorType ||
			errorCategory
		) {
			return;
		}

		addTransactionMutation.mutate({
			walletId: selectedWallet,
			type: selectedType,
			amount:
				selectedType === "income" ? Number(amount) : -Number(amount),
			currency: selectedCurrency,
			categoryId: selectedCategory,
			subcategoryId: selectedSubcategory,
			description: title,
			createdAt: selectedDate.toISOString(),
			includeInAnalysis: !markAsUnimportant,
		});

		handleClose();
	};

	const handleClose = () => {
		setSelectedDate(dayjs());
		setTitle("");
		setSelectedCategory("");
		setSelectedSubcategory("");
		setSelectedWallet("");
		setSelectedType("");
		setMarkAsUnimportant(false);
		setAmount("");
		setSelectedCurrency("");

		setErrorDate("");
		setErrorTitle("");
		setErrorAmount("");
		setErrorWallet("");
		setErrorType("");
		setErrorCategory("");
		addTransactionMutation.reset();
		onClose();
	};

	if (selectedWallet && selectedDate && walletValueOnDateQueryPending) {
		return <Loading />;
	}

	return (
		<>
			<Dialog
				open={isOpen}
				onClose={handleClose}
				sx={{ margin: "auto", padding: 0 }}
				disableEnforceFocus
			>
				<DialogTitle>{t("addCashTransaction")}</DialogTitle>
				<form
					noValidate
					onSubmit={async (event) => {
						submitForm(event);
					}}
				>
					<DialogContent sx={{ width: 500 }}>
						<FormControl fullWidth sx={{ mt: 2 }} size="small">
							<LocalizationProvider
								dateAdapter={AdapterDayjs}
								adapterLocale={userLanguage}
							>
								<DatePicker
									slotProps={{ textField: { size: "small" } }}
									value={selectedDate}
									onChange={(newValue) => {
										setSelectedDate(newValue);
									}}
									error={errorDate !== ""}
									minDate={minDate}
									maxDate={maxDate}
								/>
							</LocalizationProvider>
						</FormControl>
						{errorDate !== "" && (
							<FormHelperText
								sx={{
									ml: 2,
									color: theme.palette.error.main,
									whiteSpace: "pre-line",
								}}
							>
								{errorDate}
							</FormHelperText>
						)}

						<FormControl fullWidth sx={{ mt: 2 }} size="small">
							<TextField
								autoFocus
								required
								label={t("title")}
								type="text"
								error={errorTitle !== ""}
								helperText={errorTitle}
								value={title}
								onChange={handleTitleChange}
							/>
						</FormControl>

						<FormControl fullWidth sx={{ mt: 2 }} size="small">
							<InputLabel>{t("wallet")}</InputLabel>
							<Select
								required
								value={selectedWallet}
								label={t("wallet")}
								onChange={handleWalletChange}
								error={errorWallet !== ""}
							>
								{wallets
									.filter((item) => item.type === "cash")
									.map((wallet) => (
										<MenuItem
											key={wallet._id}
											value={wallet._id}
										>
											{wallet.name}
										</MenuItem>
									))}
							</Select>
						</FormControl>
						{errorWallet !== "" && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errorWallet}
							</FormHelperText>
						)}

						<FormControl
							fullWidth
							sx={{ mt: 2, flex: 1 }}
							size="small"
						>
							<InputLabel>{t("type")}</InputLabel>
							<Select
								required
								value={selectedType}
								label={t("type")}
								onChange={handleTypeChange}
								error={errorType !== ""}
							>
								<MenuItem value="income">
									{t("income")}
								</MenuItem>
								<MenuItem value="expense">
									{t("expense")}
								</MenuItem>
							</Select>
						</FormControl>
						{errorType !== "" && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errorType}
							</FormHelperText>
						)}

						{selectedType && (
							<CategoryDisplay
								selectedType={selectedType}
								setSelectedCategory={setSelectedCategory}
								setSelectedSubcategory={setSelectedSubcategory}
								selectedCategory={selectedCategory}
								selectedSubcategory={selectedSubcategory}
								isInModal={true}
								isError={errorCategory}
							/>
						)}
						{errorCategory !== "" && (
							<FormHelperText
								sx={{ ml: 2, color: theme.palette.error.main }}
							>
								{errorCategory}
							</FormHelperText>
						)}

						<FormControlLabel
							sx={{ mt: 2, ml: 1, flex: 1 }}
							size="small"
							control={<Checkbox />}
							label={t("markAsUnimportant")}
							onClick={(event) => {
								setMarkAsUnimportant(event.target.checked);
							}}
						/>

						<FormControl
							fullWidth
							sx={{ mt: 1, flex: 1 }}
							size="small"
						>
							<TextField
								required
								autoFocus
								label={t("balance")}
								type="text"
								value={amount}
								error={errorAmount !== ""}
								helperText={
									<span style={{ whiteSpace: "pre-line" }}>
										{errorAmount}
									</span>
								}
								slotProps={{
									input: {
										inputMode: "decimal",
										startAdornment: selectedType ===
											"expense" && (
											<InputAdornment position="start">
												-
											</InputAdornment>
										),
										endAdornment: (
											<InputAdornment position="end">
												{selectedCurrency}
											</InputAdornment>
										),
									},
								}}
								onChange={handleBalanceChange}
								onBlur={handleBlur}
							/>
						</FormControl>
					</DialogContent>
					<DialogActions>
						<Button onClick={handleClose}>{t("cancel")}</Button>
						<Button
							type="submit"
							variant="contained"
							onClick={validateInputs}
						>
							{t("save")}
						</Button>
						{addTransactionMutation.isError && (
							<p>Błąd: {addTransactionMutation.error.message}</p>
						)}
					</DialogActions>
				</form>
			</Dialog>
		</>
	);
}
